import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import CaseInfoForm from "components/CaseInfoForm";
import { ClientLens, FirmWithLenses, NamedUser } from "generated/graphql";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { createClientLensCase, selectClientLensAssignees } from "redux/slices/clientLens";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import { P } from "theme/typography";
import { caseInfoFromUI, CaseInfoUI } from "helpers/caseInfo";
import { useAllowedFirms } from "helpers/hooks/useAllowedLenses";
import { useShowSBR } from "SBR/useShowSBR";

export interface CreateCaseProps {
  onCancel: () => void;
  lens: ClientLens;
  initialAssignee?: NamedUser;
}

const existsError = "already exists";

const emptyCaseInfo: CaseInfoUI = {
  clientCaseId: "",
  claimantGivenName: "",
  claimantSurname: "",
};

const CreateCase = (props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [lens, setLens] = React.useState<ClientLens | undefined>(props.lens);
  const [caseInfo, setCaseInfo] = React.useState<CaseInfoUI>(emptyCaseInfo);
  const [assignee, setAssignee] = React.useState<NamedUser | undefined>(props.initialAssignee);
  const [selectedFirm, setSelectedFirm] = useState<FirmWithLenses | undefined>(undefined);
  const caseValidHandle = React.useState(false);
  const assignees = useAppSelector(selectClientLensAssignees);
  const [caseIdError, setCaseIdError] = React.useState<string | undefined>(undefined);
  const canCreateCase = caseInfo.clientCaseId && caseInfo.claimantGivenName.trim() && caseInfo.claimantSurname.trim();
  const firms = useAllowedFirms(lens?.id !== undefined ? [lens.id] : []);
  const sbr = useShowSBR();
  const submitCase = async () => {
    if (caseInfo && lens) {
      try {
        const lensCaseResponse = await dispatch(
          createClientLensCase({
            lensId: lens.id,
            caseInfo: caseInfoFromUI(caseInfo),
            userAssignee: sbr ? undefined : assignee?.id,
            plaintiffFirmId: selectedFirm?.id,
          })
        ).unwrap();

        navigate(`/lens/${lens.id}/cases/${lensCaseResponse.id}`);
      } catch (e: any) {
        let maybeMessageJson: { message?: string } = {};
        try {
          maybeMessageJson = JSON.parse(e.message as string);
        } catch (parseErr) {
          console.error("create case mutation failed without parseable error", e);
        }
        const isCaseIdExistsError = maybeMessageJson?.message?.includes(existsError) ?? false;
        const errorMessagePretty: string = isCaseIdExistsError
          ? "Case ID already exists"
          : "There was an error creating the case";
        if (isCaseIdExistsError) {
          setCaseIdError(errorMessagePretty);
        }
        enqueueSnackbar(errorMessagePretty, {
          variant: "error",
        });
      }
    }
  };

  const close = () => {
    props.onCancel && props.onCancel();
    setCaseInfo(emptyCaseInfo);
    setLens(undefined);
  };

  return (
    <Dialog open={true} onClose={props.onCancel} fullWidth maxWidth="sm">
      <DialogContent>
        <P variant="lgBold" sx={{ mb: 2, display: "block" }}>
          New Case for {lens?.name}
        </P>
        <CaseInfoForm
          editable
          editableId
          caseIdError={caseIdError}
          caseInfoHandle={[caseInfo, setCaseInfo]}
          assigneeHandle={[assignee, setAssignee]}
          firmHandle={[selectedFirm, setSelectedFirm]}
          validHandle={caseValidHandle}
          assigneeOptions={assignees}
          firms={firms}
        />
      </DialogContent>
      <DialogActions>
        <Button color="default" onClick={close} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Button color="default" disabled={!canCreateCase} onClick={submitCase} type="submit" variant="contained">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCase;
